import {
  observer,
  RecursionField,
  useField,
  useFieldSchema,
  useForm,
} from "@formily/react";
import { useEffect, useRef, useState } from "react";
import { IRumbleFormScope } from ".";
import { useStateMachine } from "little-state-machine";
import { Field } from "@formily/core";

const POSTAL_REGEX = /^[0-9]{4}[A-Za-z]{2}$/;

interface AddressProps {
  pathPrefix: string | null;
}

export const Address = observer((props: AddressProps) => {
  const { state } = useStateMachine();
  const form = useForm();
  const field = useField<Field>();
  const schema = useFieldSchema();

  const pathPrefix: string = props.pathPrefix ? `${props.pathPrefix}.` : "";
  const postalCode = form.getValuesIn(`${pathPrefix}postalCode`);
  const streetNumber = form.getValuesIn(`${pathPrefix}streetNumber`);

  const [errors, setErrors] = useState<string | null>(null);
  const timeoutRef = useRef<any>(null);

  // Safely extract scope and fetchData
  const scope = (state as { scope?: IRumbleFormScope }).scope;
  const fetchData = scope?.fetchData;
  if (!fetchData) {
    throw new Error("fetchData function is not available");
  }

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (!postalCode || postalCode.length !== 6 || !streetNumber) return;

      const isValid = POSTAL_REGEX.test(postalCode);
      setErrors(isValid ? null : "Postcode moet 4 cijfers en 2 letters zijn");

      if (isValid) {
        form.setValuesIn("addressErrors", true);
        fetchAddress(postalCode.toUpperCase(), streetNumber);
      }
    }, 400);
    return () => clearTimeout(timeoutRef.current);
  }, [postalCode, streetNumber]);

  const fetchAddress = async (postalCode: string, streetNumber: string) => {
    try {
      const response = await fetchData({
        endpoint: `/location`,
        queryParameters: {
          country: "NL",
          postalCode: postalCode,
          streetNumber: streetNumber,
        },
      });

      if (response.data) {
        form.setValuesIn(`${pathPrefix}city`, response.data.city);
        form.setValuesIn(`${pathPrefix}street`, response.data.street);

        setErrors("");
        clearAddressError();
        form.setValuesIn("addressErrors", false);
      } else {
        setAddressError("Incorrect address found");
      }
    } catch (err) {
      console.error("Fetch failed:", err);
      setAddressError("Unable to fetch address");
      form.setValuesIn("addressErrors", true);
    }
  };

  const setAddressError = (message: string) => {
    field.setState({
      feedbacks: [
        {
          type: "error",
          code: "validate",
          messages: [message],
        },
      ],
    });
  };

  const clearAddressError = () => {
    field.setState({
      feedbacks: [],
    });
  };

  return (
    <>
      {errors ? (
        <div style={{ color: "red", marginBottom: 8 }}>{errors}</div>
      ) : null}
      {schema?.mapProperties
        ? schema.mapProperties((schema, name) => {
            return <RecursionField key={name} name={name} schema={schema} />;
          })
        : null}
    </>
  );
});
